import { useCallback, useEffect, useRef, useState } from 'react';
import { getBacktestSettings, updateBacktestSettings } from 'api/backtestApi';
import { ApiError } from 'api/restClient';
import type { BacktestSettings, BacktestSettingsPatch } from 'types/history';

type Status = 'idle' | 'loading' | 'success' | 'error';

export interface UseBacktestSettingsResult {
  settings: BacktestSettings | null;
  status: Status;
  saving: boolean;
  error: string | null;
  save: (patch: BacktestSettingsPatch) => Promise<BacktestSettings | null>;
  reload: () => void;
}

/**
 * The persisted replay rules — the entry window, the optional time stop, and the
 * observation horizon.
 *
 * These are experiment knobs rather than deployment config, so they live in the
 * database and are edited here; the row is created with today's defaults on
 * first read, which is why this never needs a "not configured" state.
 */
export function useBacktestSettings(): UseBacktestSettingsResult {
  const [settings, setSettings] = useState<BacktestSettings | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const describe = (e: unknown): string => {
    if (e instanceof ApiError) {
      return e.status === 403
        ? 'Your account is not permitted to change backtest settings (admin only).'
        : `${e.message} (${e.status})`;
    }
    return e instanceof Error ? e.message : 'Failed to load backtest settings';
  };

  const reload = useCallback(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus('loading');
    setError(null);
    getBacktestSettings(ctrl.signal)
      .then((s) => {
        if (!mountedRef.current || ctrl.signal.aborted) return;
        setSettings(s);
        setStatus('success');
      })
      .catch((e: unknown) => {
        if (!mountedRef.current || ctrl.signal.aborted) return;
        setError(describe(e));
        setStatus('error');
      });
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = useCallback(async (patch: BacktestSettingsPatch) => {
    setSaving(true);
    setError(null);
    try {
      const next = await updateBacktestSettings(patch);
      if (mountedRef.current) setSettings(next);
      return next;
    } catch (e) {
      if (mountedRef.current) setError(describe(e));
      return null;
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, []);

  return { settings, status, saving, error, save, reload };
}
